import type { OrganizationProfile } from '../../../Generated/OrganizationProfile.ts'
import { OrganizationLogo } from './OrganizationLogo.tsx'
import { GlobeAltIcon, MapIcon } from '@heroicons/react/16/solid'
import { BeakerIcon, EnvelopeIcon } from '@heroicons/react/24/outline'

interface OrganizationWidgetProps {
  organization: OrganizationProfile
  flat?: boolean
  hideTitle?: boolean
}

export const OrganizationWidget = ({ organization, flat, hideTitle }: OrganizationWidgetProps) => {
  const renderContent = () => (
    <div className="flex flex-col md:flex-row gap-8">
      {/* Logo Section */}
      <div className="md:w-1/3 flex justify-center items-start">
        <div className="bg-white p-4 rounded-lg shadow-sm border w-full flex justify-center">
          <OrganizationLogo organization={organization} />
        </div>
      </div>

      {/* Info Section */}
      <div className="md:w-2/3 space-y-4">
        {!hideTitle && <h1 className="text-3xl font-semibold text-slate-800 leading-none">{organization.name}</h1>}
        {organization.description && <p className="text-slate-600 whitespace-pre-line">{organization.description}</p>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-700">
          <div className="flex items-start gap-2">
            <BeakerIcon className="w-5 h-5 mt-0.5 text-blue-500" />
            <div>
              <div className="font-medium">Type</div>
              <div>{organization.type}</div>
            </div>
          </div>

          {organization.country && (
            <div className="flex items-start gap-2">
              <MapIcon className="w-5 h-5 mt-0.5 text-emerald-500" />
              <div>
                <div className="font-medium">Country</div>
                <div>{organization.country}</div>
              </div>
            </div>
          )}

          {organization.homepage && (
            <div className="flex items-start gap-2">
              <GlobeAltIcon className="w-5 h-5 mt-0.5 text-indigo-500" />
              <div>
                <div className="font-medium">Homepage</div>
                <a
                  href={organization.homepage}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:underline break-all"
                >
                  {organization.homepage}
                </a>
              </div>
            </div>
          )}

          {organization.contactEmail && (
            <div className="flex items-start gap-2">
              <EnvelopeIcon className="w-5 h-5 mt-0.5 text-rose-500" />
              <div>
                <div className="font-medium">Contact Email</div>
                <a href={`mailto:${organization.contactEmail}`} className="text-blue-500 hover:underline">
                  {organization.contactEmail}
                </a>
              </div>
            </div>
          )}
        </div>

        {organization.specialities && organization.specialities.length > 0 && (
          <div>
            <div className="font-medium text-slate-700 mb-1">Specialities</div>
            <div className="flex flex-wrap gap-2">
              {organization.specialities.map((speciality, idx) => (
                <span key={idx} className="bg-slate-200 text-slate-800 text-xs px-2 py-1 rounded-full">
                  {speciality}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )

  if (flat) return renderContent()

  return (
    <div className="bg-slate-100 rounded-lg p-6 drop-shadow-sm m-auto max-w-5xl">
      <div className="bg-white p-6 rounded-lg border">{renderContent()}</div>
    </div>
  )
}
